import { useRef } from "react";
import { useInView, motion } from "framer-motion";
import { Reveal, SectionLabel } from "./primitives";
import { ASSETS } from "./data";

const CORE_CLASS = "relative w-full max-w-[300px] object-contain [mask-image:radial-gradient(ellipse_72%_74%_at_50%_46%,#000_52%,transparent_86%)]";

const OUTPUTS = [
  "Pattern Detection",
  "Confluence Engine",
  "Tier Grading",
  "AI Technical Read",
  "Whale Watch",
  "Verified Ledger",
];

export const AlgoCore = () => {
  const coreRef = useRef(null);
  const inView = useInView(coreRef, { once: true, margin: "300px" });

  return (
    <section id="algo" data-testid="algo-core" className="relative bg-[#0A0A0A] border-t border-white/[0.06] overflow-hidden">
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-72 w-72 rounded-full bg-white/[0.03] blur-[120px]" />

      <div className="relative mx-auto max-w-[1200px] px-6 md:px-12 py-16 md:py-20">
        {/* compact header */}
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><SectionLabel index="//" className="justify-center">The core</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl lg:text-4xl leading-[1.02]">
              One algo. Wired straight into the markets.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-[13px] text-white/50 leading-relaxed max-w-lg mx-auto">
              Eight strategies run 24/7 and stream graded, verified output into the live tape — so you read an edge, not a guess.
            </p>
          </Reveal>
        </div>

        {/* compact row: engine · monitor · streams */}
        <div className="mt-10 grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-8 items-center">
          {/* engine */}
          <Reveal className="order-2 lg:order-1 lg:text-right" delay={0.05}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B4BAC4] mb-2.5">// the engine</p>
            <p className="font-mono text-[12px] text-white/50 leading-relaxed lg:ml-auto max-w-[240px]">
              Independent strategies, run in parallel across the tracked universe. The core only speaks when the math agrees.
            </p>
          </Reveal>

          {/* monitor */}
          <div className="order-1 lg:order-2 relative flex justify-center">
            <Reveal delay={0.1}>
              <div ref={coreRef} className="relative flex flex-col items-center">
                <div className="absolute inset-0 lime-glow scale-[1.25] animate-pulseGlow" />
                <div className="relative">
                  {inView ? (
                    <video data-testid="algo-core-video" autoPlay loop muted playsInline preload="metadata" poster={ASSETS.computerPoster} className={CORE_CLASS}>
                      <source src={ASSETS.computer} type="video/mp4" />
                    </video>
                  ) : (
                    <img src={ASSETS.computerPoster} alt="TradrQuant algo core" className={CORE_CLASS} />
                  )}
                  {/* scanning line */}
                  <motion.div
                    className="pointer-events-none absolute left-[26%] right-[26%] h-px bg-[#BACD1C]/60"
                    style={{ boxShadow: "0 0 8px rgba(186,205,28,0.6)" }}
                    animate={{ top: ["34%", "56%", "34%"] }}
                    transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
                <div className="relative -mt-2 inline-flex items-center gap-1.5 border border-white/[0.10] bg-white/[0.03] px-2.5 py-1">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#BACD1C] opacity-70" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#BACD1C]" />
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#B4BAC4]">live · 24/7</span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* streams */}
          <Reveal className="order-3" delay={0.15}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30 mb-2.5">// streams</p>
            <ul className="space-y-2.5">
              {OUTPUTS.map((o, i) => (
                <li key={o} className="flex items-center gap-3 group">
                  <motion.span
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="hidden lg:block h-px w-8 bg-gradient-to-r from-transparent to-white/50 origin-left group-hover:to-[#BACD1C] transition-colors duration-300"
                  />
                  <span className="text-white/40 group-hover:text-[#BACD1C] font-mono text-[10px] transition-colors duration-300">◆</span>
                  <span className="font-mono text-[12px] uppercase tracking-[0.14em] text-white/65 group-hover:text-white transition-colors duration-300">{o}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
